// ===== TT Tooltips (Impact) v5 =====
// يحوّل ?{النص} أو ؟{النص} إلى Tooltip ذكي داخل أي محتوى Rich text/Prose.
(function () {
  'use strict';

  const PATTERN = /[?؟]\{([^}]+)\}/g;
  const SCOPE_SELECTORS = [
    '.product-info .prose',
    '.product-info .rte',
    '.product-info__accordion .prose',
    '.product-info__text .prose',
    '.prose',
    '.rte'
  ];
  const EXCLUDED_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'INPUT']);
  const READY_CLASS = 'tt-smart-tooltips-ready';
  const STYLE_ID = 'tt-smart-tooltip-styles';
  const OVERLAY_ID = 'tt-smart-tooltip';
  const VIEWPORT_MARGIN = 12;
  const TRIGGER_GAP = 10;

  let overlay = null;
  let overlayContent = null;
  let activeTrigger = null;
  let contentObserver = null;
  let isDestroyed = false;
  let suppressNextDismissClick = false;
  let suppressNextDismissClickTimer = null;
  const describedByValues = new WeakMap();

  function makeTooltipElement(text) {
    const tooltip = document.createElement('span');
    const question = document.createElement('span');
    const box = document.createElement('span');

    tooltip.className = 'tt';
    tooltip.tabIndex = 0;
    tooltip.setAttribute('aria-label', 'تعريف');

    question.className = 'tt-q';
    question.textContent = '❓';

    box.className = 'tt-box';
    box.textContent = text;

    tooltip.append(question, box);
    return tooltip;
  }

  function convertTextNode(textNode) {
    const text = textNode.nodeValue;

    if (!text || text.indexOf('{') === -1) return;

    PATTERN.lastIndex = 0;
    const matches = [...text.matchAll(PATTERN)];

    if (matches.length === 0) return;

    const fragment = document.createDocumentFragment();
    let currentIndex = 0;

    matches.forEach((match) => {
      const matchIndex = match.index || 0;

      if (matchIndex > currentIndex) {
        fragment.append(document.createTextNode(text.slice(currentIndex, matchIndex)));
      }

      fragment.append(makeTooltipElement(match[1]));
      currentIndex = matchIndex + match[0].length;
    });

    if (currentIndex < text.length) {
      fragment.append(document.createTextNode(text.slice(currentIndex)));
    }

    textNode.replaceWith(fragment);
  }

  function convertScope(scope) {
    if (!scope || scope.closest?.('.tt')) return;

    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;

        if (!parent || EXCLUDED_TAGS.has(parent.tagName) || parent.closest('.tt')) {
          return NodeFilter.FILTER_REJECT;
        }

        return node.nodeValue?.indexOf('{') === -1
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      }
    });
    const textNodes = [];

    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(convertTextNode);
  }

  function runOnce(root = document) {
    const scopes = new Set();

    SCOPE_SELECTORS.forEach((selector) => {
      if (root.nodeType === Node.ELEMENT_NODE && root.matches(selector)) scopes.add(root);
      root.querySelectorAll?.(selector).forEach((scope) => scopes.add(scope));
    });

    scopes.forEach(convertScope);
  }

  function installStyles() {
    document.getElementById(STYLE_ID)?.remove();

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      html.${READY_CLASS} .tt > .tt-box {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      #${OVERLAY_ID} {
        position: fixed;
        z-index: 2147483000;
        box-sizing: border-box;
        width: max-content;
        max-width: min(520px, calc(100vw - 24px));
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
        transition: opacity 140ms ease, visibility 140ms ease;
      }

      #${OVERLAY_ID}[data-open="true"] {
        visibility: visible;
        opacity: 1;
      }

      #${OVERLAY_ID} .tt-smart-tooltip__content {
        box-sizing: border-box;
        width: 100%;
        max-width: min(520px, calc(100vw - 24px));
        max-height: calc(100vh - 24px);
        padding: 9px 13px;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: none;
        -ms-overflow-style: none;
        border: 1px solid rgba(26, 26, 26, 0.09);
        border-radius: 12px;
        background: #ffffff;
        color: #454545;
        box-shadow: 0 10px 30px rgba(26, 26, 26, 0.16);
        line-height: 1.55;
        white-space: normal;
        overflow-wrap: anywhere;
      }

      #${OVERLAY_ID} .tt-smart-tooltip__content::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }

      #${OVERLAY_ID}::after {
        content: "";
        position: absolute;
        left: var(--tt-tooltip-arrow-x, 50%);
        width: 0;
        height: 0;
        margin-left: -6px;
        border: 6px solid transparent;
      }

      #${OVERLAY_ID}[data-placement="above"]::after {
        top: 100%;
        border-top-color: #ffffff;
      }

      #${OVERLAY_ID}[data-placement="below"]::after {
        bottom: 100%;
        border-bottom-color: #ffffff;
      }

      @media (prefers-reduced-motion: reduce) {
        #${OVERLAY_ID} {
          transition: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function getTooltipTrigger(target) {
    return target instanceof Element ? target.closest('.tt') : null;
  }

  function restoreDescription(trigger) {
    const previousValue = describedByValues.get(trigger);

    if (previousValue) {
      trigger.setAttribute('aria-describedby', previousValue);
    } else {
      trigger.removeAttribute('aria-describedby');
    }

    describedByValues.delete(trigger);
  }

  function hideTooltip(expectedTrigger = null) {
    /*
     * عند الانتقال سريعًا بين علامتي Tooltip، قد يصل focusout الخاص بالأولى
     * بعد أن تكون الثانية قد فُتحت. لا نسمح لحدث قديم بإغلاق النافذة الأحدث.
     */
    if (expectedTrigger && activeTrigger !== expectedTrigger) return;

    if (activeTrigger) restoreDescription(activeTrigger);

    activeTrigger = null;
    overlay?.setAttribute('aria-hidden', 'true');

    if (overlay) overlay.dataset.open = 'false';
  }

  function getPositionBoundary(trigger) {
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const boundary = {
      left: VIEWPORT_MARGIN,
      right: viewportWidth - VIEWPORT_MARGIN,
      top: VIEWPORT_MARGIN,
      bottom: viewportHeight - VIEWPORT_MARGIN
    };
    const isDesktop = window.matchMedia
      ? window.matchMedia('(min-width: 700px)').matches
      : viewportWidth >= 700;
    const chart = isDesktop ? trigger.closest('feature-chart') : null;

    if (!chart) return boundary;

    const chartRect = chart.getBoundingClientRect();
    const chartBoundary = {
      left: Math.max(boundary.left, chartRect.left + VIEWPORT_MARGIN),
      right: Math.min(boundary.right, chartRect.right - VIEWPORT_MARGIN),
      top: Math.max(boundary.top, chartRect.top + VIEWPORT_MARGIN),
      bottom: Math.min(boundary.bottom, chartRect.bottom - VIEWPORT_MARGIN)
    };

    if (chartBoundary.right - chartBoundary.left >= 160) {
      boundary.left = chartBoundary.left;
      boundary.right = chartBoundary.right;
    }

    if (chartBoundary.bottom - chartBoundary.top >= 100) {
      boundary.top = chartBoundary.top;
      boundary.bottom = chartBoundary.bottom;
    }

    return boundary;
  }

  function positionTooltip() {
    if (!activeTrigger?.isConnected || !overlay || !overlayContent) {
      hideTooltip();
      return;
    }

    const triggerRect = activeTrigger.getBoundingClientRect();
    const boundary = getPositionBoundary(activeTrigger);
    const availableWidth = Math.max(160, boundary.right - boundary.left);
    const availableHeight = Math.max(100, boundary.bottom - boundary.top);

    overlay.style.maxWidth = `${Math.round(availableWidth)}px`;
    overlayContent.style.maxWidth = `${Math.round(availableWidth)}px`;
    overlayContent.style.maxHeight = `${Math.round(availableHeight)}px`;

    const tooltipRect = overlay.getBoundingClientRect();
    const maximumLeft = Math.max(boundary.left, boundary.right - tooltipRect.width);
    const preferredLeft = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
    const left = clamp(preferredLeft, boundary.left, maximumLeft);
    const spaceAbove = triggerRect.top - boundary.top;
    const spaceBelow = boundary.bottom - triggerRect.bottom;
    const neededHeight = tooltipRect.height + TRIGGER_GAP;
    let placement = 'above';
    let top = triggerRect.top - neededHeight;

    if (spaceAbove < neededHeight && spaceBelow > spaceAbove) {
      placement = 'below';
      top = triggerRect.bottom + TRIGGER_GAP;
    }

    const maximumTop = Math.max(boundary.top, boundary.bottom - tooltipRect.height);
    top = clamp(top, boundary.top, maximumTop);

    const arrowPosition = clamp(
      triggerRect.left + triggerRect.width / 2 - left,
      16,
      Math.max(16, tooltipRect.width - 16)
    );

    overlay.dataset.placement = placement;
    overlay.style.left = `${Math.round(left)}px`;
    overlay.style.top = `${Math.round(top)}px`;
    overlay.style.setProperty('--tt-tooltip-arrow-x', `${Math.round(arrowPosition)}px`);
  }

  function showTooltip(trigger) {
    try {
      const source = trigger?.querySelector('.tt-box');
      const text = source?.textContent?.trim();

      if (!source || !text || !overlay || !overlayContent) return;

      if (activeTrigger && activeTrigger !== trigger) restoreDescription(activeTrigger);

      if (activeTrigger !== trigger) {
        const currentDescription = trigger.getAttribute('aria-describedby') || '';
        describedByValues.set(trigger, currentDescription);
        trigger.setAttribute(
          'aria-describedby',
          [currentDescription, OVERLAY_ID].filter(Boolean).join(' ')
        );
      }

      const sourceStyle = getComputedStyle(source);

      activeTrigger = trigger;
      overlayContent.textContent = text;
      overlay.style.fontFamily = sourceStyle.fontFamily;
      overlay.style.fontSize = sourceStyle.fontSize;
      overlay.style.fontWeight = sourceStyle.fontWeight;
      overlay.style.letterSpacing = sourceStyle.letterSpacing;
      overlay.style.direction = sourceStyle.direction;
      overlay.style.textAlign = sourceStyle.textAlign;
      overlay.style.visibility = 'hidden';
      overlay.style.opacity = '0';
      overlay.dataset.open = 'true';
      overlay.setAttribute('aria-hidden', 'false');

      positionTooltip();

      overlay.style.removeProperty('visibility');
      overlay.style.removeProperty('opacity');
    } catch (error) {
      destroy();
    }
  }

  function handleMouseOver(event) {
    const trigger = getTooltipTrigger(event.target);

    if (trigger && !trigger.contains(event.relatedTarget)) showTooltip(trigger);
  }

  function handleMouseOut(event) {
    const trigger = getTooltipTrigger(event.target);

    if (
      trigger &&
      !trigger.contains(event.relatedTarget) &&
      !trigger.contains(document.activeElement)
    ) {
      hideTooltip(trigger);
    }
  }

  function handleFocusIn(event) {
    const trigger = getTooltipTrigger(event.target);

    if (trigger) showTooltip(trigger);
  }

  function handleFocusOut(event) {
    const trigger = getTooltipTrigger(event.target);

    if (!trigger) return;

    requestAnimationFrame(() => {
      if (!trigger.matches(':hover') && !trigger.contains(document.activeElement)) {
        hideTooltip(trigger);
      }
    });
  }

  function handleClick(event) {
    const trigger = getTooltipTrigger(event.target);

    if (trigger) {
      showTooltip(trigger);
    } else {
      hideTooltip();
    }
  }

  function armDismissClickSuppression() {
    suppressNextDismissClick = true;
    window.clearTimeout(suppressNextDismissClickTimer);
    suppressNextDismissClickTimer = window.setTimeout(() => {
      suppressNextDismissClick = false;
      suppressNextDismissClickTimer = null;
    }, 800);
  }

  function handleClickCapture(event) {
    if (!suppressNextDismissClick) return;

    suppressNextDismissClick = false;
    window.clearTimeout(suppressNextDismissClickTimer);
    suppressNextDismissClickTimer = null;

    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function handlePointerDown(event) {
    if (!activeTrigger || activeTrigger.contains(event.target)) return;

    /*
     * النقرة الأولى خارج النافذة تغلقها فقط، فلا تصل إلى الصف أو الزر الموجود
     * تحتها. نستثني Tooltip آخر كي يمكن الانتقال إليه مباشرة بنقرة واحدة.
     */
    if (!getTooltipTrigger(event.target)) armDismissClickSuppression();
    hideTooltip();
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') hideTooltip();
  }

  function handleViewportChange() {
    if (activeTrigger) positionTooltip();
  }

  function destroy() {
    if (isDestroyed) return;

    isDestroyed = true;
    hideTooltip();
    contentObserver?.disconnect();
    document.documentElement.classList.remove(READY_CLASS);
    document.removeEventListener('mouseover', handleMouseOver);
    document.removeEventListener('mouseout', handleMouseOut);
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('focusout', handleFocusOut);
    document.removeEventListener('click', handleClickCapture, true);
    document.removeEventListener('click', handleClick);
    document.removeEventListener('pointerdown', handlePointerDown);
    document.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    window.visualViewport?.removeEventListener('resize', handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', handleViewportChange);
    window.clearTimeout(suppressNextDismissClickTimer);
    suppressNextDismissClick = false;
    suppressNextDismissClickTimer = null;
    overlay?.remove();
    document.getElementById(STYLE_ID)?.remove();
  }

  function init() {
    window.__ttSmartTooltipsDestroy?.();
    isDestroyed = false;
    runOnce();
    installStyles();

    overlay = document.createElement('div');
    overlayContent = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.dataset.open = 'false';
    overlay.setAttribute('role', 'tooltip');
    overlay.setAttribute('aria-hidden', 'true');
    overlayContent.className = 'tt-smart-tooltip__content';
    overlay.appendChild(overlayContent);
    document.body.appendChild(overlay);

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('click', handleClickCapture, true);
    document.addEventListener('click', handleClick);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('scroll', handleViewportChange, { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', handleViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', handleViewportChange, { passive: true });

    contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) runOnce(node);
        });
      });
    });
    contentObserver.observe(document.documentElement, { childList: true, subtree: true });

    document.documentElement.classList.add(READY_CLASS);
    window.__ttSmartTooltipsDestroy = destroy;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
